'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Plus, Layout, SlidersHorizontal, Check } from 'lucide-react';
import { PageHeader } from '@/shared/components/layout/page-header';
import { AiBriefing } from '@/features/dashboard/components/ai-briefing';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { usePermissions } from '@/features/auth/hooks/use-permissions';
import { MonitorPerformanceWidget } from '@/features/dashboard/widgets/monitor/monitor-widget';
import { WidgetGrid, DEFAULT_LAYOUT } from '@/features/dashboard/components/widget-grid';
import { useDashboardLayout } from '@/features/dashboard/hooks/use-dashboard-layout';
import { WidgetPicker } from '@/features/dashboard/components/widget-picker';
import { UsageWidget } from '@/features/billing/components/usage-widget';
import { SetupChecklistWidget } from '@/features/onboarding/components/setup-checklist-widget';
import { F082DeadlineBanner } from '@/features/settings/components/f082-deadline-banner';
import { CaenMigrationBanner } from '@/features/settings/components/caen-migration-banner';
import { VatThresholdBanner } from '@/features/dashboard/components/vat-threshold-banner';

function BriefingSlot() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const isAdmin = role === 'owner' || role === 'admin';
  const enabled = user?.briefingEnabled !== false;
  if (!isAdmin || !enabled) return null;
  return <AiBriefing />;
}

export default function DashboardPage() {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { can } = usePermissions();
  // SINGURA instanță de useDashboardLayout — partajată cu WidgetGrid prin
  // props. Istoric: pagina avea propria instanță cu default WIDGET_ORDER
  // (toate widget-urile) pe aceeași cheie localStorage → cele două stări se
  // suprascriau reciproc (picker desincronizat de grid, default-ul lean v2
  // anulat la reload, reset-ul readucea TOATE widget-urile).
  const { layout, hydrated, setOrder, hideWidget, showWidget, setWidgetSpan, reset: resetLayout } = useDashboardLayout(DEFAULT_LAYOUT);
  const visible = layout.visible;
  const hidden = layout.hidden;

  // Modul „Aranjează": grip-urile de drag + X-urile apar doar la cerere —
  // permanent vizibile erau zgomot pe un dashboard de citit zilnic. ESC iese.
  const [editMode, setEditMode] = useState(false);
  useEffect(() => {
    if (!editMode) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEditMode(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode]);

  // Feedback la adăugarea din picker: cardul nou ateriza la finalul paginii
  // fără niciun semnal → la închiderea picker-ului derulăm la primul widget
  // adăugat și-l evidențiem scurt (clasa dash-widget-flash, vezi widget-grid).
  const addedInSession = useRef<string[]>([]);
  const handlePickerAdd = (id: string) => {
    addedInSession.current.push(id);
    showWidget(id);
  };
  const handlePickerClose = () => {
    setPickerOpen(false);
    const added = addedInSession.current;
    addedInSession.current = [];
    if (added.length === 0) return;
    setTimeout(() => {
      const first = document.querySelector(`[data-widget-id="${added[0]}"]`);
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      for (const id of added) {
        const el = document.querySelector(`[data-widget-id="${id}"]`);
        el?.classList.add('dash-widget-flash');
        setTimeout(() => el?.classList.remove('dash-widget-flash'), 3400);
      }
    }, 150);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        showBack
        title="Dashboard"
        actions={
          <>
            <button
              onClick={() => setEditMode((v) => !v)}
              title={editMode ? 'Ieși din modul de aranjare (ESC)' : 'Reordonează sau ascunde widget-uri prin tragere'}
              className={
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ' +
                (editMode
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800')
              }
            >
              {editMode ? <Check className="h-4 w-4" /> : <SlidersHorizontal className="h-4 w-4" />}
              {editMode ? 'Gata' : 'Aranjează'}
            </button>
            <button
              onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              <Layout className="h-4 w-4" />
              Widget-uri
              {hidden.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[0.625rem] font-bold ml-1">
                  {hidden.length}
                </span>
              )}
            </button>
            {can('invoices.create') && (
              <Link
                href="/facturi/nou"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Factură nouă
              </Link>
            )}
          </>
        }
      />

      {/* L57 — Banner Formular 082 ANAF (PFA/II/IF only) */}
      <F082DeadlineBanner />
      <CaenMigrationBanner />
      {/* Plafonul de scutire TVA (art. 310) + facturile în valută fără curs.
          Componenta exista de mult, dar nu era montată NICĂIERI — deci nici
          alerta de plafon nu a ajuns vreodată la vreun utilizator. Se ascunde
          singură când n-are ce spune. */}
      <VatThresholdBanner />

      {/* Onboarding checklist — auto-detect 14 setup items */}
      <SetupChecklistWidget />

      {/* Sumar briefing — doar pentru admin/owner și dacă e activat */}
      <BriefingSlot />

      {/* Monitor Performanță — widget fix full-width */}
      <div className="grid grid-cols-12 gap-4">
        <MonitorPerformanceWidget />
      </div>

      {/* Usage (billing) */}
      <UsageWidget />

      {/* Draggable widgets grid — starea vine din instanța unică de mai sus */}
      <WidgetGrid layout={layout} hydrated={hydrated} setOrder={setOrder} hideWidget={hideWidget} editMode={editMode} onResize={setWidgetSpan} />

      {/* Widget picker modal */}
      <WidgetPicker
        open={pickerOpen}
        visible={visible}
        onClose={handlePickerClose}
        onAdd={handlePickerAdd}
        onReset={() => {
          if (confirm('Revii la afișarea standard a dashboard-ului? Personalizările tale se pierd.')) {
            resetLayout();
            setPickerOpen(false);
          }
        }}
      />

    </div>
  );
}
